import { Suspense, useEffect, useState } from "react";
import { AppShellSidebar } from "./AppShellSidebar";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Rows } from "@/components/ui/icons";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Badge } from "@/components/ui/badge";
import { TerminalResponseLinkProvider } from "@/lib/terminal-response-link-context";
import { IS_LOCAL_DEV } from "@/lib/config";
import { APP_SHELL_RING_CLASSES } from "@/lib/surface-styles";
import { cn } from "@/lib/utils";
import type { InviteAcceptSuccess } from "@ano/shared/api/invites";
import {
  AutomationsPage,
  ConnectedToolsPage,
  InboxView,
  InvitesView,
  LibraryView,
  ListView,
  ListsOverview,
  SkillsPage,
  TeamView,
  TerminalPanel,
} from "./lazy-imports";
import { MultiplexLayer } from "./MultiplexLayer";
import { ViewLoading } from "./view-loading";

interface ConnectionStateLike {
  name: string;
}

interface WorkspaceLike {
  id: string;
  name?: string | null;
  slug?: string | null;
}

export interface AppShellNavProps {
  scope: string;
  selectedWorkspace: string | null;
  selectedChannel: string | null;
  selectedListId: string | null;
  showLists: boolean;
  showCoworkers: boolean;
  showAutomations: boolean;
  showSkills: boolean;
  showConnectedTools: boolean;
  showLibrary: boolean;
  showInvites: boolean;
  handleScopeChange: (newScope: string) => void;
  setSelectedChannel: (id: string | null) => void;
  setShowLists: (v: boolean) => void;
  setShowCoworkers: (v: boolean) => void;
  setShowAutomations: (v: boolean) => void;
  setShowSkills: (v: boolean) => void;
  setShowConnectedTools: (v: boolean) => void;
  setShowLibrary: (v: boolean) => void;
  setShowInvites: (v: boolean) => void;
  setSelectedListId: (v: string | null) => void;
  navigateToSubdomain: (slug: string) => boolean;
  handleSelectWorkspace: (id: string | null) => void;
  openChannelWithContext: (
    channelId: string,
    opts?: {
      workspaceId?: string | null;
      targetMessageId?: string;
      targetThreadId?: string;
    },
  ) => void;
  handleToggleLists: () => void;
  handleToggleCoworkers: () => void;
  handleToggleAutomations: () => void;
  handleToggleInvites: () => void;
  setScope: (s: string) => void;
  workspaceList: WorkspaceLike[];
  setPendingWorkspace: (id: string | null) => void;
  setMembershipLostWorkspace: (name: string | null) => void;
  setListItemCreatePreselect: (id: string | undefined) => void;
  setShowListItemCreate: (v: boolean) => void;
}

export interface AppShellViewFlags {
  isListActive: boolean;
  isListsOverviewActive: boolean;
  isTeamActive: boolean;
  isAutomationsActive: boolean;
  isSkillsActive: boolean;
  isConnectedToolsActive: boolean;
  isLibraryActive: boolean;
  isInvitesActive: boolean;
  isMultiplexActive: boolean;
  isInboxActive: boolean;
}

export interface AppShellMultiplexProps {
  mountedIds: ReadonlyArray<string>;
  channelWorkspaceMap: ReadonlyMap<string, string | null>;
  targetMessageId: string | null;
  targetThreadId: string | null;
  handleSelectChannelForMessageArea: (id: string) => void;
  handleToggleTerminal: () => void | Promise<void>;
  handleTargetMessageHandled: () => void;
  handleSelectList: (id: string | null) => void;
}

export interface AppShellInboxProps {
  inviteToken: string | null;
  handleInviteNavigation: (result: InviteAcceptSuccess) => void;
  handleInviteTokenConsumed: () => void;
}

export interface AppShellTerminalProps {
  terminalWorkspaceId: string | null;
  terminalOpen: boolean;
  hasOpenedTerminal: boolean;
  terminalWidth: number;
  setTerminalWidth: (w: number) => void;
  handleOpenWorkspaceSettings: () => void;
  handleCycleViewMode: () => void | Promise<void>;
}

interface AppShellProps {
  connectionState: ConnectionStateLike;
  isConnected: boolean;
  appVersion: string;
  showSidebar: boolean;
  showChat: boolean;
  nav: AppShellNavProps;
  views: AppShellViewFlags;
  multiplex: AppShellMultiplexProps;
  inbox: AppShellInboxProps;
  terminal: AppShellTerminalProps;
}

/**
 * Render-only shell — the giant return body of `AppInner`. Every state
 * owner stays in the parent; this component only consumes props and
 * renders.
 *
 * The multiplex MessageArea layer rendered here MUST stay a persistent
 * sibling toggled via `className` (`contents` / `hidden`) — see
 * `MultiplexLayer.tsx` for the contract.
 */
export function AppShell({
  connectionState,
  isConnected,
  appVersion,
  showSidebar,
  showChat,
  nav,
  views,
  multiplex,
  inbox,
  terminal,
}: AppShellProps) {
  // Mobile (< md): Sidebar mounts inside a Sheet drawer instead of as a
  // persistent flex column. Auto-closes on workspace/channel change,
  // and also on viewport widen / showSidebar→false so a stale `open`
  // doesn't snap back when the user returns to a mobile viewport.
  const isMobile = useIsMobile();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  useEffect(() => {
    // Intentional setState-in-effect: syncing the sheet's open flag
    // with the upstream navigation cursor + responsive flips. Same
    // pattern as `use-sidebar-preferences.ts` localStorage hydration.
    // Without this the drawer stays open after the user picks a
    // channel, OR pops open again when the user rotates back to
    // portrait after rotating to landscape with it open.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMobileSidebarOpen(false);
  }, [
    nav.selectedChannel,
    nav.selectedWorkspace,
    nav.scope,
    isMobile,
    showSidebar,
  ]);

  // On the channel view, MessageAreaHeader renders its own in-header
  // hamburger (md:hidden). Showing the AppShell-level floater there too
  // would stack two hamburgers at top-left. Limit the floater to views
  // that have NO header hamburger of their own: Inbox, Lists, Team,
  // Library, Automations, Skills, Connected Tools, and the empty-multiplex
  // (no channel selected) state.
  const channelHeaderHasHamburger =
    views.isMultiplexActive && !!nav.selectedChannel;
  const showFloatingHamburger =
    isMobile && showSidebar && !mobileSidebarOpen && !channelHeaderHasHamburger;

  const sidebar = showSidebar ? (
    <AppShellSidebar nav={nav} inDrawer={isMobile} />
  ) : null;

  return (
    <div
      className={cn(
        "app-shell flex h-screen h-[100dvh] text-foreground relative rounded-[10px] overflow-hidden",
        APP_SHELL_RING_CLASSES,
      )}
    >
      {showFloatingHamburger && (
        <div className="absolute top-1.5 left-2 z-40">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Open sidebar"
            onClick={() => setMobileSidebarOpen(true)}
            className="h-9 w-9 text-foreground/80 hover:bg-foreground/10"
          >
            <Rows size={18} />
          </Button>
        </div>
      )}
      <div className="absolute top-2 right-2 z-50 flex items-center gap-1.5 pointer-events-none">
        {IS_LOCAL_DEV && (
          <Badge
            variant="outline"
            className="border-warning/40 bg-warning-muted/70 text-warning"
            title="Local dev stack: embedded Postgres, local Zero, local API, local ano config"
          >
            LOCAL
          </Badge>
        )}
        <div
          className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] text-transparent max-w-32 sm:max-w-48 truncate"
          title={`Sync: ${connectionState.name} | v${appVersion}`}
        >
          <div
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${
              isConnected ? "bg-success" : "bg-destructive"
            }`}
          />
          {!isConnected && connectionState.name}
        </div>
      </div>
      {isMobile && sidebar ? (
        <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
          <SheetContent
            side="left"
            showClose={false}
            className="p-0 w-[260px] max-w-none flex flex-col"
          >
            {sidebar}
          </SheetContent>
        </Sheet>
      ) : (
        sidebar
      )}
      {showChat && (
        <>
          <MultiplexLayer
            selectedChannel={nav.selectedChannel}
            selectedWorkspace={nav.selectedWorkspace}
            isMultiplexActive={views.isMultiplexActive}
            mountedIds={multiplex.mountedIds}
            channelWorkspaceMap={multiplex.channelWorkspaceMap}
            targetMessageId={multiplex.targetMessageId}
            targetThreadId={multiplex.targetThreadId}
            handleSelectChannelForMessageArea={
              multiplex.handleSelectChannelForMessageArea
            }
            handleToggleTerminal={multiplex.handleToggleTerminal}
            handleTargetMessageHandled={multiplex.handleTargetMessageHandled}
            onOpenMobileSidebar={
              isMobile && showSidebar
                ? () => setMobileSidebarOpen(true)
                : undefined
            }
          />

          {views.isListActive && (
            <ErrorBoundary>
              <Suspense fallback={<ViewLoading label="list" />}>
                <ListView
                  listId={nav.selectedListId!}
                  workspaceId={nav.selectedWorkspace}
                  onClose={() => {
                    nav.setSelectedListId(null);
                    nav.setShowLists(true);
                  }}
                />
              </Suspense>
            </ErrorBoundary>
          )}

          {views.isListsOverviewActive && (
            <ErrorBoundary>
              <Suspense fallback={<ViewLoading label="tables" />}>
                <ListsOverview
                  workspaceId={nav.selectedWorkspace}
                  onSelectList={multiplex.handleSelectList}
                />
              </Suspense>
            </ErrorBoundary>
          )}

          {views.isTeamActive && (
            <ErrorBoundary>
              <Suspense fallback={<ViewLoading label="team" />}>
                <TeamView
                  workspaceId={nav.selectedWorkspace!}
                  onClose={() => nav.setShowCoworkers(false)}
                />
              </Suspense>
            </ErrorBoundary>
          )}

          {views.isLibraryActive && (
            <ErrorBoundary>
              <Suspense fallback={<ViewLoading label="integrations" />}>
                <LibraryView
                  workspaceId={nav.selectedWorkspace ?? undefined}
                  onClose={() => nav.setShowLibrary(false)}
                />
              </Suspense>
            </ErrorBoundary>
          )}

          {views.isAutomationsActive && (
            <ErrorBoundary>
              <Suspense fallback={<ViewLoading label="automations" />}>
                <AutomationsPage workspaceId={nav.selectedWorkspace} />
              </Suspense>
            </ErrorBoundary>
          )}

          {views.isInvitesActive && (
            <ErrorBoundary>
              <Suspense fallback={<ViewLoading label="invites" />}>
                <InvitesView
                  openChannelWithContext={nav.openChannelWithContext}
                  onScopeChange={nav.handleScopeChange}
                />
              </Suspense>
            </ErrorBoundary>
          )}

          {views.isSkillsActive && (
            <ErrorBoundary>
              <Suspense fallback={<ViewLoading label="skills" />}>
                <SkillsPage workspaceId={nav.selectedWorkspace!} />
              </Suspense>
            </ErrorBoundary>
          )}

          {views.isConnectedToolsActive && (
            <ErrorBoundary>
              <Suspense fallback={<ViewLoading label="connected tools" />}>
                <ConnectedToolsPage workspaceId={nav.selectedWorkspace!} />
              </Suspense>
            </ErrorBoundary>
          )}

          {views.isInboxActive && (
            <ErrorBoundary>
              <Suspense fallback={<ViewLoading label="inbox" />}>
                <InboxView
                  openChannelWithContext={nav.openChannelWithContext}
                  workspaceList={nav.workspaceList}
                  inviteToken={inbox.inviteToken}
                  onInviteAccepted={inbox.handleInviteNavigation}
                  onInviteTokenConsumed={inbox.handleInviteTokenConsumed}
                  scope={nav.scope}
                  onScopeChange={nav.handleScopeChange}
                />
              </Suspense>
            </ErrorBoundary>
          )}
        </>
      )}
      {terminal.terminalWorkspaceId && terminal.hasOpenedTerminal && (
        <ErrorBoundary>
          <Suspense
            fallback={
              <div
                className={
                  terminal.terminalOpen
                    ? "shrink-0 h-full bg-background"
                    : "hidden"
                }
                style={{ width: terminal.terminalWidth }}
              />
            }
          >
            <TerminalResponseLinkProvider
              workspaceId={terminal.terminalWorkspaceId}
              openChannelWithContext={nav.openChannelWithContext}
            >
              <div className={terminal.terminalOpen ? "contents" : "hidden"}>
                <TerminalPanel
                  workspaceId={terminal.terminalWorkspaceId}
                  channelId={nav.selectedChannel}
                  onClose={multiplex.handleToggleTerminal}
                  onOpenSettings={terminal.handleOpenWorkspaceSettings}
                  panelWidth={terminal.terminalWidth}
                  onPanelWidthChange={terminal.setTerminalWidth}
                  chatCollapsed={!showChat}
                  sidebarCollapsed={!showSidebar}
                  onToggleChatCollapsed={terminal.handleCycleViewMode}
                />
              </div>
            </TerminalResponseLinkProvider>
          </Suspense>
        </ErrorBoundary>
      )}
    </div>
  );
}
